"use client";
import { useRef, type Ref } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import cn from "clsx";

import PartnerLogo from "@/shared/components/PartnerLogo";
import { PARTNERS, type Partner } from "@/shared/data/partners";

import s from "./styles.module.css";

gsap.registerPlugin(useGSAP, ScrollTrigger);

/** Marquee travel speed, px per second. Slow enough to read each name. */
const SPEED = 55;

/** Marks render at 150% of their tuned height in the row. */
const SCALE = 1.5;

interface GroupProps {
  partners: Partner[];
  ref?: Ref<HTMLDivElement>;
  /** The duplicate exists only to hide the seam, so keep it off the a11y tree. */
  duplicate?: boolean;
}

const Group = ({ partners, ref, duplicate = false }: GroupProps) => (
  <div className={s.group} ref={ref} aria-hidden={duplicate || undefined}>
    {partners.map((partner) => (
      <PartnerLogo key={partner.name} partner={partner} scale={SCALE} />
    ))}
  </div>
);

interface Props {
  className?: string;
  /** Travel right instead of left. */
  reverse?: boolean;
  /**
   * Rotate the roster so the row leads with a different partner. Paired with
   * `reverse` it keeps two rows from ever showing the same mark alongside
   * each other.
   */
  startAt?: number;
  /**
   * Hides the row from assistive tech. Set it on every row after the first:
   * they repeat the same roster, and a screen reader should read it once.
   */
  decorative?: boolean;
}

export default function PartnersMarquee({
  className,
  reverse = false,
  startAt = 0,
  decorative = false,
}: Props) {
  const root = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const group = useRef<HTMLDivElement>(null);
  const tween = useRef<gsap.core.Tween | null>(null);

  const offset = ((startAt % PARTNERS.length) + PARTNERS.length) % PARTNERS.length;
  const partners = [...PARTNERS.slice(offset), ...PARTNERS.slice(0, offset)];

  useGSAP(
    () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      const trackEl = track.current;
      const groupEl = group.current;
      if (!trackEl || !groupEl) return;

      // Only run while the row is on screen. A forever-looping transform
      // otherwise repaints the whole row for as long as the page is open.
      let offscreen = false;
      const sync = () => {
        if (!tween.current) return;
        if (offscreen) tween.current.pause();
        else tween.current.play();
      };

      // The track holds two identical groups, so shifting it by exactly half
      // its width lands the second group where the first began: the seam is
      // invisible and the tween just repeats. Reversing simply swaps the two
      // ends of that shift, which stays seamless for the same reason.
      // Duration comes from the measured width so the logos travel at the same
      // speed on every breakpoint, where the gaps (and so the distance) differ.
      const build = () => {
        tween.current?.kill();
        tween.current = null;

        // Zero width means we cannot derive a duration yet: the row is in a
        // hidden tab or a display:none ancestor. Bail rather than build a
        // zero-duration tween that repeats forever; the ResizeObserver below
        // calls back once the row has a real width.
        const width = groupEl.offsetWidth;
        if (!width) return;

        gsap.set(trackEl, { xPercent: reverse ? -50 : 0 });
        tween.current = gsap.to(trackEl, {
          xPercent: reverse ? 0 : -50,
          duration: width / SPEED,
          ease: "none",
          repeat: -1,
        });
        sync();
      };

      build();

      // This row replaced a block that wrapped onto several lines, so the
      // section is shorter and everything below it moves up. Other sections on
      // this page are driven by ScrollTrigger, which caches each trigger's
      // start/end as absolute scroll positions when it is created — if any of
      // those were measured before this row settled, they fire at the wrong
      // place and the sections below appear misplaced. Recompute them once the
      // row has its final size.
      let refreshed = false;
      const refreshTriggers = () => {
        if (refreshed) return;
        refreshed = true;
        ScrollTrigger.refresh();
      };
      if (tween.current) refreshTriggers();

      // Logo widths settle once their images decode, and the gaps change at
      // the 768px breakpoint. Both change the distance to travel.
      const ro = new ResizeObserver(() => {
        build();
        if (tween.current) refreshTriggers();
      });
      ro.observe(groupEl);

      const io = new IntersectionObserver(
        ([entry]) => {
          offscreen = !entry.isIntersecting;
          sync();
        },
        { rootMargin: "200px" }
      );
      io.observe(trackEl);

      return () => {
        ro.disconnect();
        io.disconnect();
        tween.current?.kill();
      };
    },
    { scope: root, dependencies: [reverse] }
  );

  return (
    <div
      ref={root}
      className={cn(s.viewport, className)}
      aria-hidden={decorative || undefined}
    >
      <div ref={track} className={s.track}>
        <Group partners={partners} ref={group} />
        <Group partners={partners} duplicate />
      </div>
    </div>
  );
}
